/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/require-await -- the mock implements an async
   (Promise-returning) interface synchronously; keeping the methods `async` turns their
   guard-clause throws into promise rejections, exactly as the real API interceptor does. */

import { addDays, addHours, addMinutes, startOfDay } from "date-fns"
import { MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import {
  BookingAccount,
  BookingAppointment,
  BookingConfig,
  BookingRequest,
  BookingResult,
  BookingSession,
  BookingSlot,
  BookingVerifyResult,
  QuestionnaireDefinition,
  QuestionnaireQuestion,
} from "@medaica/common/types"
import { BookingApiClient } from "./booking-api-client"

/**
 * Faithful in-memory mock of the 03 §3 booking contract — the wizard is fully
 * demoable and testable against it with no backend. Errors are shaped exactly
 * like the real response interceptor output (`isMedaicaApiError` + numeric
 * `type`) so the wizard's error handling is identical against mock and real.
 * See INTEGRATION-NOTES for the assumptions to re-verify against real M4.
 */
const mockApiError = (
  type: MedaicaApiErrorType,
  extra?: Record<string, unknown>
): Error & { isMedaicaApiError: true; type: MedaicaApiErrorType } => {
  const err = new Error(MedaicaApiErrorType[type]) as Error & { isMedaicaApiError: true; type: MedaicaApiErrorType }
  err.isMedaicaApiError = true
  err.type = type
  if (extra) {
    Object.assign(err, extra)
  }
  return err
}

// Demo triggers (mock only — see INTEGRATION-NOTES).
const CORRECT_CODE = "123456"
const EXPIRED_CODE = "000000"
const INVALID_TOKENS = new Set(["expired", "invalid"])
const MAX_CODE_ATTEMPTS = 5
const SESSION_MINUTES = 30
const CODE_MINUTES = 10
// Booking window/exam constants the mock reports via getBookingConfig — the slot generation
// below reads the same minLeadHours so the mock's slots match what it advertises. 168h (7 days)
// mirrors the real backend's lead (03 §4).
const BOOKING_CONFIG: BookingConfig = { examMinutes: 30, slotMinutes: 45, minLeadHours: 168, horizonWeeks: 8 }

// 45-min grid matching the PDF p15 layout; 12:45 & 15:30 are capacity 0 (rendered as a
// struck-through, disabled/unavailable chip).
const SLOT_TIMES: Array<[number, number]> = [
  [9, 0],
  [9, 45],
  [10, 30],
  [11, 15],
  [12, 0],
  [12, 45],
  [14, 0],
  [14, 45],
  [15, 30],
  [16, 15],
]
const FULL_SLOT_TIMES = new Set(["12:45", "15:30"])

// No/Yes/Not sure — the only option set the current single-select questionnaire uses.
const NO = { value: "no", label: "No" }
const YES = { value: "yes", label: "Yes" }
const NOT_SURE = { value: "not_sure", label: "Not sure" }
const YES_NO_UNSURE = [NO, YES, NOT_SURE]

// Patient-facing intro (the real prose is QA-owned and served by the API; this mirrors the
// served shape for the demo/offline flow).
const QUESTIONNAIRE_INTRO = [
  "These questions help your Medaica care team understand your heart health before your at-home exam.",
  "Answer what you can — there are no right or wrong answers, and the technician can go over anything " +
    "with you during the exam.",
]

// Medaica D2C Health Questionnaire v2 shape (OTIS-868) — the real prose is QA-owned and
// served by the API; this mock mirrors the served SHAPE (number kinds with unit/min/max,
// a multi with an exclusive "prefer not to answer" and an "other" free-text companion via
// show_if) so the demo/offline flow exercises every renderer branch.
const QUESTIONNAIRE_V2: QuestionnaireQuestion[] = [
  {
    key: "height_ft",
    section: "Height",
    kind: "number",
    text: "What is your height?",
    unit: "ft",
    min: 1,
    max: 8,
  },
  {
    key: "height_in",
    section: "Height",
    kind: "number",
    text: "What is your height?",
    unit: "in",
    min: 0,
    max: 11,
  },
  {
    key: "weight_lbs",
    section: "Weight",
    kind: "number",
    text: "What is your weight?",
    unit: "lbs",
    min: 1,
    max: 1500,
  },
  {
    key: "race_ethnicity",
    section: "Race and Ethnicity",
    kind: "multi",
    text: "How do you describe your race and/or ethnicity? Select all that apply.",
    options: [
      { value: "asian", label: "Asian" },
      { value: "black_african_american", label: "Black or African American" },
      { value: "hispanic_latino", label: "Hispanic or Latino" },
      { value: "white", label: "White" },
      { value: "other", label: "Another race or ethnicity", other: true },
      { value: "prefer_not_to_answer", label: "Prefer not to answer", exclusive: true },
    ],
  },
  {
    key: "race_ethnicity_other",
    section: "Race and Ethnicity",
    kind: "text",
    text: "Another race or ethnicity:",
    show_if: { key: "race_ethnicity", in: ["other"] },
  },
  {
    key: "heart_rhythm",
    section: "Heart Rhythm",
    kind: "choice",
    text: "Have you noticed an irregular, racing, fluttering, or skipped heartbeat?",
    options: YES_NO_UNSURE,
  },
  {
    key: "chest_discomfort",
    section: "Chest Discomfort",
    kind: "choice",
    text: "Have you experienced chest pain, pressure, or tightness?",
    options: YES_NO_UNSURE,
  },
  {
    key: "high_blood_pressure",
    section: "High Blood Pressure",
    kind: "choice",
    text: "Have you ever been told by a healthcare professional that you have high blood pressure (hypertension)?",
    options: YES_NO_UNSURE,
  },
  {
    key: "family_history",
    section: "Family History",
    kind: "choice",
    text: "Do you have a family history of heart disease?",
    options: YES_NO_UNSURE,
  },
]

const buildQuestionnaire = (): QuestionnaireDefinition => ({
  version: 2,
  intro: QUESTIONNAIRE_INTRO,
  questions: QUESTIONNAIRE_V2,
})

class MockBookingApi implements BookingApiClient {
  private readonly maskedEmail = "jo•••••son@gmail.com"
  private readonly verificationToken = "mock-verification-token"
  private codeExpiresAt: Date = addMinutes(new Date(), CODE_MINUTES)
  private codeAttempts = 0

  private sessionExpiresAt: Date | null = null
  private examsAvailable = 3
  private nextExamRefNumber = 2102
  private slotTakenSimulated = false // simulate one lost booking race, once per session
  private appointments: BookingAppointment[] = [
    {
      examRef: "EX-2101",
      patientName: "Anna Johnson",
      slotStart: addDays(new Date(), 8),
      timeZone: "America/New_York",
      status: "scheduled",
      videoRoomUrl: "https://patient.medaica.com/live-exam/mock-2101",
    },
    {
      examRef: "EX-2088",
      patientName: "John Johnson",
      slotStart: addDays(new Date(), -10),
      timeZone: "America/New_York",
      status: "completed",
      videoRoomUrl: null,
    },
  ]

  // --- anonymous (pre-session) ---

  async createSession(params: { token?: string; email?: string }): Promise<BookingSession> {
    if (params.token && INVALID_TOKENS.has(params.token)) {
      throw mockApiError(MedaicaApiErrorType.INVALID_BOOKING_TOKEN)
    }
    this.codeExpiresAt = addMinutes(new Date(), CODE_MINUTES)
    this.codeAttempts = 0
    return {
      maskedEmail: this.maskedEmail,
      codeExpiresAt: this.codeExpiresAt,
      verificationToken: this.verificationToken,
    }
  }

  async verify(_verificationToken: string, code: string): Promise<BookingVerifyResult> {
    if (new Date() > this.codeExpiresAt || code === EXPIRED_CODE) {
      throw mockApiError(MedaicaApiErrorType.CODE_EXPIRED)
    }
    if (code === CORRECT_CODE) {
      this.sessionExpiresAt = addMinutes(new Date(), SESSION_MINUTES)
      return { accessToken: "mock-session-token", expiresIn: SESSION_MINUTES * 60 }
    }
    this.codeAttempts += 1
    if (this.codeAttempts >= MAX_CODE_ATTEMPTS) {
      throw mockApiError(MedaicaApiErrorType.CODE_LOCKED)
    }
    // Wrong-but-unlocked code is not enumerated in 03 §3 — mock uses INVALID_DATA +
    // attemptsRemaining; confirm the real M4 shape (INTEGRATION-NOTES).
    throw mockApiError(MedaicaApiErrorType.INVALID_DATA, { attemptsRemaining: MAX_CODE_ATTEMPTS - this.codeAttempts })
  }

  async resendCode(_verificationToken: string): Promise<void> {
    this.codeExpiresAt = addMinutes(new Date(), CODE_MINUTES)
    this.codeAttempts = 0
  }

  // --- session-scoped ---

  private assertSession(): void {
    if (!this.sessionExpiresAt || new Date() > this.sessionExpiresAt) {
      throw mockApiError(MedaicaApiErrorType.EXPIRED_SESSION)
    }
  }

  async getAccount(): Promise<BookingAccount> {
    this.assertSession()
    return {
      buyerFirstName: "John",
      buyerLastName: "Doe",
      buyerEmail: "john.doe@example.com",
      buyerPhone: "+1 (555) 123-4567",
      examsAvailable: this.examsAvailable,
      // Mirror the real backend: expiry drops to null once the pool is empty
      // (its earliest-expiry query filters qty_remaining > 0).
      earliestExpiry: this.examsAvailable > 0 ? new Date("2027-07-31T00:00:00Z") : null,
      appointments: [...this.appointments],
    }
  }

  async getQuestionnaire(): Promise<QuestionnaireDefinition> {
    this.assertSession()
    return buildQuestionnaire()
  }

  async getAvailableSlots(from: Date, to: Date, _timeZone: string): Promise<BookingSlot[]> {
    this.assertSession()
    // Enforce the configured lead like the real engine, so no past/too-soon slots are
    // ever offered (a demo must not book an exam in the past).
    const earliest = addHours(new Date(), BOOKING_CONFIG.minLeadHours)
    const slots: BookingSlot[] = []
    // Half-open [from, to) to match slot-paging.pageWindow — `<= to` would emit the next page's
    // first calendar day too, overlapping slots across adjacent page windows.
    for (let day = startOfDay(from); day < to; day = addDays(day, 1)) {
      for (const [hours, minutes] of SLOT_TIMES) {
        const startUtc = addMinutes(startOfDay(day), hours * 60 + minutes)
        if (startUtc < earliest) {
          continue
        }
        const full = FULL_SLOT_TIMES.has(`${hours}:${minutes.toString().padStart(2, "0")}`)
        slots.push({ startUtc, capacity: full ? 0 : 2 })
      }
    }
    return slots
  }

  async getBookingConfig(): Promise<BookingConfig> {
    this.assertSession()
    return { ...BOOKING_CONFIG }
  }

  async bookAppointment(request: BookingRequest): Promise<BookingResult> {
    this.assertSession()
    if (this.examsAvailable <= 0) {
      throw mockApiError(MedaicaApiErrorType.NO_CREDITS)
    }
    if (!this.slotTakenSimulated) {
      // Simulate one lost race per session (03 §5, "that time just filled"); any
      // retry — same slot or another — then books.
      this.slotTakenSimulated = true
      throw mockApiError(MedaicaApiErrorType.SLOT_TAKEN)
    }
    const examRef = `EX-${this.nextExamRefNumber}`
    this.nextExamRefNumber += 1
    this.examsAvailable -= 1
    this.appointments = [
      {
        examRef,
        patientName: `${request.patient.firstName} ${request.patient.lastName}`,
        slotStart: request.slotStart,
        timeZone: request.timeZone,
        status: "scheduled",
        videoRoomUrl: `https://patient.medaica.com/live-exam/mock-${examRef}`,
      },
      ...this.appointments,
    ]
    return {
      examRef,
      slotStart: request.slotStart,
      timeZone: request.timeZone,
      joinNote: "Your join link is in the confirmation email.",
      examsRemaining: this.examsAvailable,
    }
  }

  // Test/demo helper — force the 30-min session expiry to exercise the resume flow (05 §1).
  expireSession(): void {
    this.sessionExpiresAt = new Date(Date.now() - 1000)
  }
}

export default MockBookingApi
